//replaces widget-title.html
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";

import { SWReactIcons } from "@similarweb/icons";
import { AutosizeInput } from "@similarweb/ui-components";

import DashboardService from "app/components/dashboard/services/DashboardService";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { getWidgetTitle } from "app/components/widget/widget-utilities/widgetPpt/PptWidgetUtils";
import { SwTrack } from "app/services/SwTrack";

// interface IWidgetProps extends
interface IWidgetTileTemplate extends IDashboardWidgetInterface {
    titleEditable: boolean;
    titleClickable: boolean;
    widgetTitle: any;
    editTitle: () => any;
    cancelEditTitle: () => any;
    saveTitle: () => any;
    onWidgetTitleChange: () => void;
}

// Replaces widget-title.html
export const WidgetTitleTemplate: FC<IWidgetTileTemplate> = (props) => {
    const {
        widget,
        //  titleEditable,
        titleClickable,
        widgetTitle,
        cancelEditTitle,
        saveTitle,
        editTitle,
        onWidgetTitleChange,
    } = props;

    const { viewData, viewOptions, dashboardId, data, apiParams, readOnly } = widget;

    const { showTitle, titlePaddingBottom, titleClass, titleType, inlineBorderless, showTitleTooltip } = viewOptions;
    const isSharedWithMe = DashboardService.dashboard?.isSharedWithMe;

    const { titleIcon, proUrl, title, tooltip } = viewData;
    const [titleEditable, setTitleEditable] = useState(false);
    const [currentDashboard, setCurrentDashboard] = useState(null);
    const shouldDisplayNonPreviewGAVerifiedContainer = useMemo(() => {
        return (
            widget &&
            widget.isCompare() &&
            widget.dashboardId &&
            !titleEditable &&
            widget.data?.isGAVerified &&
            widget.dashboardId !== DASHBOARD_ID_PREVIEW_MODE
        );
    }, [widget, titleEditable]);

    const shouldDisplayDashboardPreviewGAVerifiedContainer = useMemo(() => {
        return (
            widget?.dashboardId &&
            !titleEditable &&
            !titleClickable &&
            widget.data?.isGAVerified &&
            widget.dashboardId === DASHBOARD_ID_PREVIEW_MODE
        );
    }, [widget, titleEditable, titleClickable]);

    const shouldDisplayNonDashboardPreviewGAVerifiedContainer = useMemo(() => {
        return !widget.isCompare() && !widget.dashboardId && widget.data.isGAVerified;
    }, [widget]);

    const [shouldDisplayIcon, setShouldDisplayIcon] = useState(false);
    const [shouldDisplayTextTitle, setShouldDisplayTextTitle] = useState(false);
    const [widgetTitleInput, setWidgetTitleInput] = useState(getWidgetTitle(widget));

    const shouldDisplayLink = useMemo(() => {
        return !!titleClickable && titleType === "link" && dashboardId !== DASHBOARD_ID_PREVIEW_MODE;
    }, [titleClickable, titleType, dashboardId]);

    const shouldDisplayGAVerifiedContainer = useMemo(() => {
        return dashboardId === DASHBOARD_ID_PREVIEW_MODE && !titleEditable && !titleClickable && data?.isGAVerified;
    }, [dashboardId, titleEditable, data?.isGAVerified, titleClickable]);

    useEffect(() => {
        const displayIcon = dashboardId && titleClickable && !readOnly;
        setShouldDisplayIcon(displayIcon);
    }, [dashboardId, titleClickable, readOnly]);

    useEffect(() => {
        const displayText = titleType === "text" || dashboardId === DASHBOARD_ID_PREVIEW_MODE || !titleClickable;
        setShouldDisplayTextTitle(displayText);
    }, [titleType, dashboardId, titleClickable]);

    // Todo: need to verify with Elad/ Uri/ Adi if we want to keep existing behaviour of selecting title text when focused.
    //  If yes, we simply need to add the following support to Autosizeinput component: if (this.props.autoSelectWhenFocused) {
    //       this.inputRef?.select();
    //     }

    function handleEditTitle() {
        setTitleEditable(true);
    }

    function handleSaveTitle() {
        setTitleEditable(false);
        const updatedWidget = widget._widgetConfig;
        // change title on config
        updatedWidget.properties.title = widgetTitleInput;
        // change title on widget instance
        widget.mergeViewData({ title: widgetTitleInput });
        // change template to 'custom'
        updatedWidget.properties.titleTemplate = "custom";
        DashboardService.editWidget(updatedWidget);
        SwTrack.all.trackEvent("Edit", "click", "widget name/" + widgetTitleInput);
    }
    return widget.viewOptions.showTitle ? (
        <div
            style={{
                display: "inline-flex",
                paddingBottom: widget.viewOptions.titlePaddingBottom ? widget.viewOptions.titlePaddingBottom : "15px",
            }}
            className={classNames("grid-no-drag swWidgetTitle", widget.viewOptions.titleClass)}
        >
            {widget.viewData.titleIcon ? (
                <div className={widget.viewData.titleIcon}>
                    <SWReactIcons
                        className={`swHeader-title-icon`}
                        iconName={widget.viewData.titleIcon}
                    />
                </div>
            ) : null}
            {shouldDisplayLink && !titleEditable ? (
                <a
                    href={proUrl}
                    className="swHeader-title is-link"
                >
                    {widgetTitleInput}
                </a>
            ) : null}
            {titleEditable ? (
                <AutosizeInput
                    placeholder=""
                    className={classNames("swHeader-title-input editTitleinput", {
                        hideInput: !titleEditable,
                    })}
                    maxLength={42}
                    value={widgetTitleInput}
                    onApply={handleSaveTitle}
                    onCancel={cancelEditTitle}
                    onChange={(e) => setWidgetTitleInput(e.target.value)}
                    onFocus={(event) => {
                        event.target.select();
                    }}
                    autoFocus
                ></AutosizeInput>
            ) : null}
            {shouldDisplayNonPreviewGAVerifiedContainer ? (
                <GAVerifiedContainer
                    size="SMALL"
                    isActive={true}
                    isPrivate={false}
                    tooltipAvailable={true}
                    tooltipIsActive={false}
                    metric={widget?.apiParams?.metric}
                />
            ) : null}
            {widget.dashboardId && titleClickable && !widget.readOnly && !isSharedWithMe ? (
                <i
                    className={classNames("sw-icon-edit sw-icon-edit-title-dashboard", {
                        hiddenImportant: titleEditable,
                    })}
                    onClick={handleEditTitle}
                />
            ) : null}
            {widget.viewOptions.titleType === "text" ||
            widget.dashboardId === DASHBOARD_ID_PREVIEW_MODE ||
            !titleClickable ? (
                <span className="swHeader-title">{widgetTitle}</span>
            ) : null}
            {shouldDisplayDashboardPreviewGAVerifiedContainer ? (
                <GAVerifiedContainer
                    size="SMALL"
                    isActive={true}
                    isPrivate={false}
                    tooltipAvailable={true}
                    tooltipIsActive={false}
                    metric={""}
                />
            ) : null}
            {widget.viewOptions.showTitleTooltip ? (
                <span
                    className="swHeader-tooltip scss-tooltip scss-tooltip--s"
                    data-scss-tooltip={widget.viewData.tooltip}
                >
                    <SWReactIcons
                        className={`centered-flex`}
                        iconName={"info"}
                    />
                </span>
            ) : null}
            {shouldDisplayNonDashboardPreviewGAVerifiedContainer ? (
                <div className="centered-flex">
                    <GAVerifiedContainer
                        size="SMALL"
                        isActive={true}
                        isPrivate={!!widget.data?.isGAPrivate}
                        tooltipAvailable={true}
                        tooltipIsActive={false}
                        metric={widget.apiParams.metric}
                    />
                </div>
            ) : null}
        </div>
    ) : null;
};
